import React, { useState, useEffect, useCallback } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import ReCAPTCHA from 'react-google-recaptcha'
import axios from '../../../src/api'
import { VERIFY_HUMAN } from '../../../src/api/constants'
import { Response } from '../../typedef/api'
import {
  CAPTCHA_V2_ENABLE,
  CAPTCHA_V3_ENABLE,
  CAPTCHA_V2_KEY,
  CAPTCHA_V3_KEY,
} from 'api/config'
import {
  isReCaptchaFeatureEnabled,
  reCaptchaVersion,
  reCaptchaFeature,
} from './ParkerLinkReCaptcha'

interface HybridRecaptchaProps {
  onValidationSuccess: () => void
  onValidationFailure?: () => void
  onTokenReceived: (token: string) => void
  feature: reCaptchaFeature
}

const HybridRecaptchaContent: React.FC<HybridRecaptchaProps> = ({
  onValidationSuccess,
  onValidationFailure,
  onTokenReceived,
  feature,
}): React.ReactElement => {
  const [showRecaptchaV2, setShowRecaptchaV2] = useState(false)
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleRecaptchaV3 = useCallback(async (): Promise<void> => {
    if (!CAPTCHA_V3_ENABLE && CAPTCHA_V2_ENABLE) {
      setShowRecaptchaV2(true)
      return
    }

    if (!CAPTCHA_V3_ENABLE || !isReCaptchaFeatureEnabled(feature)) {
      onValidationSuccess()
      return
    }

    if (isRecaptchaValid || showRecaptchaV2) {
      return
    }

    if (!CAPTCHA_V3_KEY || !executeRecaptcha) {
      console.warn('reCAPTCHA v3 is not ready.')
      setTimeout(handleRecaptchaV3, 1000)
      return
    }

    try {
      const token = await executeRecaptcha('verify_user')
      const isValid = await validateRecaptcha(token, reCaptchaVersion.v3)

      if (isValid) {
        setIsRecaptchaValid(true)
        onTokenReceived(token)
        onValidationSuccess()
        sessionStorage.setItem('recaptchaToken', token)
      } else if (CAPTCHA_V2_ENABLE) {
        setShowRecaptchaV2(true)
      } else {
        onValidationFailure?.()
      }
    } catch (error) {
      console.error('Error during reCAPTCHA v3 validation:', error)
    }
  }, [
    executeRecaptcha,
    onValidationSuccess,
    onValidationFailure,
    onTokenReceived,
    showRecaptchaV2,
    feature,
    isRecaptchaValid,
  ])

  const handleRecaptchaV2 = useCallback(
    async (token: string | null): Promise<void> => {
      if (!isReCaptchaFeatureEnabled(feature)) {
        onValidationSuccess()
        return
      }
      if (!token) {
        onValidationFailure?.()
        return
      }

      try {
        const isValid = await validateRecaptcha(token, reCaptchaVersion.v2)

        if (isValid) {
          setIsRecaptchaValid(true)
          onTokenReceived(token)
          onValidationSuccess()
          sessionStorage.setItem('recaptchaToken', token)
        } else {
          onValidationFailure?.()
        }
      } catch (error) {
        console.error('Error during reCAPTCHA v2 validation:', error)
      }
    },
    [onValidationSuccess, onValidationFailure, onTokenReceived, feature]
  )

  const validateRecaptcha = async (
    token: string,
    version: reCaptchaVersion
  ): Promise<boolean> => {
    try {
      const response = await axios.post<Response<boolean>>(`${VERIFY_HUMAN}`, {
        token: token,
        version: version,
      })
      return response.data?.data ?? false
    } catch (error) {
      console.error(`reCAPTCHA ${version} validation error:`, error)
      return false
    }
  }

  useEffect(() => {
    if (isRecaptchaValid) {
      return
    }
    if (executeRecaptcha) {
      handleRecaptchaV3()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeRecaptcha])

  return (
    <div>
      {showRecaptchaV2 && CAPTCHA_V2_KEY && (
        <ReCAPTCHA sitekey={CAPTCHA_V2_KEY} onChange={handleRecaptchaV2} />
      )}
    </div>
  )
}

interface AppProps {
  onValidationSuccess: () => void
  onValidationFailure?: () => void
  onTokenReceived: (token: string) => void
  feature: reCaptchaFeature
}

const HybridRecaptcha: React.FC<AppProps> = ({
  onValidationSuccess,
  onValidationFailure,
  onTokenReceived,
  feature,
}) => (
  <HybridRecaptchaContent
    onValidationSuccess={onValidationSuccess}
    onValidationFailure={onValidationFailure}
    onTokenReceived={onTokenReceived}
    feature={feature}
  />
)

export default HybridRecaptcha
